import {useEffect, useState} from 'react';
import type {ReactAction} from '@netx/core/bootstrap';
import {useTimeout} from '@netx/core-components/hooks/useTimeout';
import {useActionMenuProvider} from '../contexts';

export type UseSubMenuNextParams = {
	depth: number;
};

export const useActionSubMenu = ({depth}: UseSubMenuNextParams) => {
	const {activeDepth, mainMenuOpen, handleDepthLeft, setActiveDepth} =
		useActionMenuProvider();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [activeAction, setActiveAction] = useState<ReactAction | null>(null);

	const {executeTimeout: executeItemTimeout, cancelTimeout: cancelItemTimeout} =
		useTimeout({timeout: 200});

	const {executeTimeout: executeMenuTimeout, cancelTimeout: cancelMenuTimeout} =
		useTimeout({timeout: 500});

	useEffect(() => {
		if (activeDepth < depth) {
			setAnchorEl(null);
		}
	}, [activeDepth, depth]);

	const setMenuInteraction = (interacting = true) => {
		interacting && setActiveDepth(depth);
	};

	const startMenuInteraction = () => {
		setMenuInteraction(true);
		cancelStopMenuInteraction();
	};

	const cancelStopMenuInteraction = () => {
		cancelMenuTimeout();
	};

	const stopMenuInteraction = () => {
		cancelStopMenuInteraction();
		executeMenuTimeout(() => {
			handleDepthLeft(depth);
		});
	};

	const setItemInteraction = (action: ReactAction, anchorEl: HTMLElement) => {
		setActiveAction(action);
		setAnchorEl(anchorEl);
		setMenuInteraction(true);
	};

	const startItemInteraction = (action: ReactAction, anchorEl: HTMLElement) => {
		cancelItemTimeout();
		if (!action.children?.length) {
			executeItemTimeout(() => {
				setAnchorEl(null);
			}, 300);
			return;
		}
		executeItemTimeout(() => {
			setItemInteraction(action, anchorEl);
		});
	};

	const stopItemInteraction = (action: ReactAction) => {
		cancelItemTimeout();
	};

	return {
		anchorEl,
		activeAction,
		mainMenuOpen,
		setItemInteraction,
		startItemInteraction,
		stopItemInteraction,

		cancelStopMenuInteraction,
		setMenuInteraction,
		startMenuInteraction,
		stopMenuInteraction,
	};
};
